import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckLine, WarningLine, AlertOctagonLine, InformationLine, CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';

const alertVariants = cva(
  'flex items-start gap-3 rounded-[var(--radius-md)] border p-3 text-sm',
  {
    variants: {
      variant: {
        success:
          'bg-[var(--color-success-light)] border-[var(--color-success)] text-[var(--color-success-dark)]',
        warning:
          'bg-[var(--color-warning-light)] border-[var(--color-warning)] text-[var(--color-warning-dark)]',
        danger:
          'bg-[var(--color-danger-light)] border-[var(--color-danger)] text-[var(--color-danger-dark)]',
        info:
          'bg-[var(--color-info-light)] border-[var(--color-info)] text-[var(--color-info-dark)]',
      },
    },
    defaultVariants: { variant: 'info' },
  },
);

const ICONS = {
  success: CheckLine,
  warning: WarningLine,
  danger: AlertOctagonLine,
  info: InformationLine,
} as const;

export interface InlineAlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
}

export const InlineAlert = React.forwardRef<HTMLDivElement, InlineAlertProps>(
  ({ className, variant = 'info', title, dismissible, onDismiss, children, ...props }, ref) => {
    const Icon = ICONS[variant ?? 'info'];
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        <Icon size={20} className="shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-0">
          {title ? <div className="font-semibold leading-tight">{title}</div> : null}
          {children ? <div className={cn(title && 'mt-1')}>{children}</div> : null}
        </div>
        {dismissible ? (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 rounded-[var(--radius-sm)] p-1 opacity-70 hover:opacity-100 hover:bg-black/5"
            aria-label="Dismiss"
          >
            <CloseLine size={14} />
          </button>
        ) : null}
      </div>
    );
  },
);
InlineAlert.displayName = 'InlineAlert';
